import React, { lazy, Suspense } from 'react';
import { getCachedCalculatorResult } from './calculatorCache';
import { Box, CircularProgress, Typography } from '@mui/material';

// Prefetch queue to manage loading
const prefetchQueue = new Set();
const loadedCalculators = new Set();
const prefetchObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const calculatorId = entry.target.dataset.calculatorId;
        if (calculatorId && !loadedCalculators.has(calculatorId)) {
          prefetchCalculator(calculatorId);
        }
        prefetchObserver.unobserve(entry.target);
      }
    });
  },
  { rootMargin: '200px' } // Start prefetching when calculator is 200px from viewport
);

/**
 * Prefetch a calculator component
 * @param {string} calculatorId - The ID of the calculator to prefetch
 */
export const prefetchCalculator = (calculatorId) => {
  if (prefetchQueue.has(calculatorId) || loadedCalculators.has(calculatorId)) return;
  
  prefetchQueue.add(calculatorId);
  
  // Use dynamic import to prefetch the calculator
  import(`../components/calculators/${calculatorId}/${calculatorId}Calculator.js`)
    .then(() => {
      loadedCalculators.add(calculatorId);
      prefetchQueue.delete(calculatorId);
      console.log(`Prefetched calculator: ${calculatorId}`);
    })
    .catch(err => {
      console.error(`Failed to prefetch calculator: ${calculatorId}`, err);
      prefetchQueue.delete(calculatorId);
    });
};

/**
 * Register an element for calculator prefetching
 * @param {HTMLElement} element - The element to observe for intersection
 * @param {string} calculatorId - The calculator ID to prefetch when element is visible
 */
export const registerForPrefetch = (element, calculatorId) => {
  if (!element || !calculatorId) return;
  
  element.dataset.calculatorId = calculatorId;
  prefetchObserver.observe(element);
};

/**
 * Lazily load a calculator component with automatic error handling and retries
 * @param {string} calculatorId - The ID of the calculator to load
 * @param {Object} options - Additional options
 * @returns {React.LazyExoticComponent} The lazy-loaded component
 */
export const lazyCalculatorComponent = (calculatorId, options = {}) => {
  const {
    fallback = <div className="calculator-loading">Loading calculator...</div>,
    maxRetries = 2,
    cacheKey = null
  } = options;

  // Factory function for lazy loading with retries
  const loadComponent = () => {
    let retries = 0;
    
    const load = async () => {
      try {
        // Use dynamic import for code splitting
        const component = await import(`../components/calculators/${calculatorId}/${calculatorId}Calculator.js`);
        loadedCalculators.add(calculatorId);
        return component;
      } catch (err) {
        if (retries < maxRetries) {
          retries++;
          console.log(`Retrying load for calculator: ${calculatorId} (${retries}/${maxRetries})`);
          return load();
        }
        throw err;
      }
    };
    
    return load();
  };
  
  // Create the lazy component
  const LazyComponent = lazy(loadComponent);
  
  // Return a wrapped component that handles suspense and data prefetching
  return (props) => {
    // If cache key is provided, try to get cached data
    const cachedData = cacheKey ? getCachedCalculatorResult(calculatorId, cacheKey) : null;
    
    return (
      <Suspense fallback={fallback}>
        <LazyComponent {...props} cachedData={cachedData} />
      </Suspense>
    );
  };
};

/**
 * Lazily load a calculator sub-component (for more granular code splitting)
 * @param {string} calculatorId - The ID of the parent calculator
 * @param {string} componentName - The name of the sub-component
 * @param {Object} options - Additional options
 * @returns {React.LazyExoticComponent} The lazy-loaded component
 */
export const lazyCalculatorSubComponent = (calculatorId, componentName, options = {}) => {
  const {
    fallback = <div className="component-loading">Loading...</div>,
    maxRetries = 1
  } = options;

  // Factory function for lazy loading with retries
  const loadComponent = () => {
    let retries = 0;
    
    const load = async () => {
      try {
        // Use dynamic import for code splitting
        return await import(`../components/calculators/${calculatorId}/components/${componentName}.js`);
      } catch (err) {
        if (retries < maxRetries) {
          retries++;
          return load();
        }
        throw err;
      }
    };
    
    return load();
  };
  
  // Create and return the lazy component with suspense
  const LazyComponent = lazy(loadComponent);
  
  return (props) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

/**
 * Preload essential calculators on app initialization
 * This should be called when the app starts to preload frequently used calculators
 */
export const preloadEssentialCalculators = () => {
  // List of essential calculators to preload
  const essentialCalculators = [
    'bmi', 
    'percentage', 
    'tip'
  ];
  
  // Preload each calculator in the background
  essentialCalculators.forEach(calculatorId => {
    // Use requestIdleCallback if available, or setTimeout as fallback
    const schedulePreload = window.requestIdleCallback || 
      ((cb) => setTimeout(cb, 1000));
    
    schedulePreload(() => prefetchCalculator(calculatorId));
  });
};

export default {
  lazyCalculatorComponent,
  lazyCalculatorSubComponent,
  prefetchCalculator,
  registerForPrefetch,
  preloadEssentialCalculators
};